import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.prima.myx.is';

interface CreateIssueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface IssueFormData {
  title: string;
  body: string;
  labels: string[];
}

const initialFormData: IssueFormData = {
  title: '',
  body: '',
  labels: [],
};

const SUGGESTED_LABELS = [
  'bug',
  'enhancement',
  'documentation',
  'question',
  'urgent',
  'needs-investigation',
];

export function CreateIssueModal({ open, onOpenChange }: CreateIssueModalProps) {
  const [formData, setFormData] = useState<IssueFormData>(initialFormData);
  const [labelInput, setLabelInput] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch GitHub configuration
  const { data: config } = useQuery({
    queryKey: ['github-config'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/issues/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch GitHub config');
      }

      return response.json();
    },
    enabled: open,
  });

  // Fetch current quota
  const { data: quota } = useQuery({
    queryKey: ['github-quota'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/issues/quota`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch quota');
      }

      return response.json();
    },
    enabled: open && config?.configured,
  });

  // Create issue mutation
  const createIssueMutation = useMutation({
    mutationFn: async (data: IssueFormData) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/issues`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create issue');
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'Issue Created',
        description: (
          <div className="flex flex-col gap-2">
            <p>GitHub issue #{data.issue.github_issue_number} created successfully</p>
            <a
              href={data.issue.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              View on GitHub →
            </a>
          </div>
        ),
      });

      queryClient.invalidateQueries({ queryKey: ['github-issues'] });
      queryClient.invalidateQueries({ queryKey: ['github-quota'] });

      // Reset form and close modal
      setFormData(initialFormData);
      setLabelInput('');
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title is required',
        variant: 'destructive',
      });
      return;
    }

    createIssueMutation.mutate(formData);
  };

  const handleAddLabel = (label: string) => {
    const trimmedLabel = label.trim().toLowerCase();

    if (!trimmedLabel) return;

    if (formData.labels.length >= 10) {
      toast({
        title: 'Maximum Labels',
        description: 'You can add up to 10 labels',
        variant: 'destructive',
      });
      return;
    }

    if (formData.labels.includes(trimmedLabel)) {
      toast({
        title: 'Duplicate Label',
        description: 'This label is already added',
        variant: 'destructive',
      });
      return;
    }

    setFormData({
      ...formData,
      labels: [...formData.labels, trimmedLabel],
    });
    setLabelInput('');
  };

  const handleRemoveLabel = (label: string) => {
    setFormData({
      ...formData,
      labels: formData.labels.filter(l => l !== label),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLabel(labelInput);
    }
  };

  // Check if GitHub is configured
  if (open && config && !config.configured) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>GitHub Not Configured</DialogTitle>
            <DialogDescription>
              Please configure your GitHub Personal Access Token and repository in Settings before creating issues.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button onClick={() => {
              onOpenChange(false);
              window.location.href = '/settings';
            }}>
              Go to Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5" />
            Create GitHub Issue
          </DialogTitle>
          <DialogDescription>
            Create a new issue in {config?.github_repo || 'your repository'}
            {quota && (
              <span className="ml-2 text-sm">
                ({quota.quota.remaining} of {quota.quota.limit} issues remaining today)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="required">
              Title
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Brief description of the issue"
              required
              maxLength={256}
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label htmlFor="body">Description</Label>
            <Textarea
              id="body"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              placeholder="Detailed description of the issue (optional)"
              rows={6}
              maxLength={65536}
            />
            <p className="text-xs text-gray-500">
              Supports Markdown formatting
            </p>
          </div>

          {/* Labels */}
          <div className="space-y-2">
            <Label htmlFor="labels">Labels</Label>

            {/* Current labels */}
            {formData.labels.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.labels.map((label) => (
                  <Badge
                    key={label}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => handleRemoveLabel(label)}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Add label input */}
            <div className="flex gap-2">
              <Input
                id="labels"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add label (press Enter)"
                maxLength={50}
                disabled={formData.labels.length >= 10}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => handleAddLabel(labelInput)}
                disabled={!labelInput.trim() || formData.labels.length >= 10}
              >
                Add
              </Button>
            </div>

            {/* Suggested labels */}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-xs text-gray-500">Suggested:</span>
              {SUGGESTED_LABELS.filter(l => !formData.labels.includes(l)).map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleAddLabel(label)}
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                  disabled={formData.labels.length >= 10}
                >
                  + {label}
                </button>
              ))}
            </div>
          </div>

          {/* Quota warning */}
          {quota && quota.quota.remaining <= 2 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                ⚠️ You have {quota.quota.remaining} issue creation{quota.quota.remaining !== 1 ? 's' : ''} remaining today.
                Quota resets at {new Date(quota.quota.resetsAt).toLocaleTimeString()}.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createIssueMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createIssueMutation.isPending || !formData.title.trim()}
            >
              {createIssueMutation.isPending ? 'Creating...' : 'Create Issue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
